import { useMemo, useState } from 'react'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { useClientPagination } from '../../../hooks/useClientPagination'
import { Pagination } from '../../../components/ui/Pagination'
import type { Eticheta, EticheteStatus } from './types'
import { STATUS_LABELS, STATUS_COLORS } from './types'

interface Props {
  etichete: Eticheta[]
  canWrite: boolean
  onEdit: (e: Eticheta) => void
  onPreview: (e: Eticheta) => void
  onUpdateStatus: (id: number, status: EticheteStatus) => void
  onDelete: (id: number) => void
  onNew: () => void
}

export function RegistruTab({
  etichete,
  canWrite,
  onEdit,
  onPreview,
  onUpdateStatus,
  onDelete,
  onNew,
}: Props) {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<EticheteStatus | ''>('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return etichete.filter((e) => {
      if (statusFilter && e.status !== statusFilter) return false
      if (q) {
        const m =
          e.idIntern.toLowerCase().includes(q) ||
          e.denumire.toLowerCase().includes(q) ||
          (e.ean ?? '').toLowerCase().includes(q)
        if (!m) return false
      }
      return true
    })
  }, [etichete, search, statusFilter])

  const pag = useClientPagination(filtered, 10)

  return (
    <Card description="Toate etichetele create. Apasă „Editează” ca să reiei o etichetă. Etichetele aprobate sau tipărite nu pot fi șterse — trece-le în starea „Arhivată”.">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="search"
          placeholder="Caută după ID, denumire, EAN…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] rounded border border-b4l-border px-3 py-1.5 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as EticheteStatus | '')}
          className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
        >
          <option value="">Toate stările</option>
          {(Object.keys(STATUS_LABELS) as EticheteStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        {canWrite && (
          <Button onClick={onNew} className="ml-auto !px-3 !py-1.5 !text-xs">
            + Etichetă nouă
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          {etichete.length === 0
            ? 'Nicio etichetă în registru. Creează una din fila Generator.'
            : 'Nicio etichetă nu corespunde filtrelor.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">ID</th>
                <th className="py-2 pr-3 font-medium">Denumire</th>
                <th className="py-2 pr-3 font-medium">Tip</th>
                <th className="py-2 pr-3 font-medium">Dimensiuni</th>
                <th className="py-2 pr-3 font-medium">EAN</th>
                <th className="py-2 pr-3 font-medium">Stare</th>
                <th className="py-2 pr-3 font-medium">Creat</th>
                <th className="py-2 pr-3 font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {pag.paginated.map((e) => (
                <tr key={e.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-[11px] text-b4l-purple-dark">
                    {e.idIntern}
                  </td>
                  <td className="py-2 pr-3 max-w-[280px] truncate" title={e.denumire}>
                    {e.denumire}
                  </td>
                  <td className="py-2 pr-3 text-xs">{e.tip}</td>
                  <td className="py-2 pr-3 text-xs">
                    {e.dimW && e.dimH ? `${e.dimW} x ${e.dimH} mm` : '—'}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px]">{e.ean ?? '—'}</td>
                  <td className="py-2 pr-3">
                    {canWrite ? (
                      <select
                        value={e.status}
                        onChange={(ev) => onUpdateStatus(e.id, ev.target.value as EticheteStatus)}
                        className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[e.status]}`}
                      >
                        {(Object.keys(STATUS_LABELS) as EticheteStatus[]).map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[e.status]}`}
                      >
                        {STATUS_LABELS[e.status]}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-[11px] text-b4l-text-muted">
                    {new Date(e.createdAt).toLocaleDateString('en-GB')}
                    {e.creatDe && (
                      <div className="text-[10px] text-b4l-text-dim">{e.creatDe.name}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex gap-1">
                      <Button
                        variant="secondary"
                        onClick={() => onPreview(e)}
                        className="!px-2 !py-1 !text-xs"
                      >
                        Previzualizează
                      </Button>
                      {canWrite && (
                        <Button
                          variant="secondary"
                          onClick={() => onEdit(e)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Editează
                        </Button>
                      )}
                      {canWrite && e.status !== 'Aprobata' && e.status !== 'Tiparita' && (
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Ștergi eticheta ${e.idIntern} - ${e.denumire}?`))
                              onDelete(e.id)
                          }}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Șterge
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3">
        <Pagination
          page={pag.page}
          pageSize={pag.pageSize}
          total={pag.total}
          onPageChange={pag.setPage}
          onPageSizeChange={pag.setPageSize}
          itemLabel="etichete"
        />
      </div>
    </Card>
  )
}
